import { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Target,
  Shield,
  AlertTriangle,
  Loader2,
  DollarSign,
  BarChart3,
  Brain,
  Scale,
  Zap,
  Activity,
  Globe
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bias colors
const BIAS_CONFIG = {
  LONG: { color: "bg-green-500", icon: TrendingUp, text: "BULLISH" },
  SHORT: { color: "bg-red-500", icon: TrendingDown, text: "BEARISH" },
  NEUTRAL: { color: "bg-gray-500", icon: Minus, text: "NEUTRAL" },
};

// Format price based on instrument
const formatPrice = (price, instrument) => {
  if (!price) return "—";
  if (instrument === "USDJPY") return price.toFixed(3);
  return price.toFixed(5);
};

export const AuraFXPage = ({ instrument, name, apiPath }) => {
  const [thesis, setThesis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchThesis();
    fetchConfig();
  }, [instrument]);

  const fetchThesis = async () => {
    try {
      const response = await axios.get(`${API}${apiPath}/thesis`);
      if (response.data.available) {
        setThesis(response.data.thesis);
      } else {
        setThesis(null);
      }
    } catch (error) {
      console.error("Error fetching thesis:", error);
      toast.error("Failed to fetch thesis");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}${apiPath}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}${apiPath}/refresh`);
      setThesis(response.data.thesis);
      toast.success("Thesis generated successfully!");
    } catch (error) {
      console.error("Error refreshing:", error);
      toast.error(error.response?.data?.detail || "Failed to generate thesis");
    } finally {
      setRefreshing(false);
    }
  };

  const biasConfig = BIAS_CONFIG[thesis?.bias] || BIAS_CONFIG.NEUTRAL;
  const BiasIcon = biasConfig.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading {name} analysis...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`aura-${instrument.toLowerCase()}-page`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary" />
            AuraTrade {name}
          </h1>
          <p className="text-muted-foreground text-sm">
            COT-driven weekly swing thesis with multi-agent debate
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-gradient-to-r from-blue-500 to-cyan-500"
          data-testid={`${instrument.toLowerCase()}-refresh-btn`}
        >
          {refreshing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating (~60s)...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              {thesis ? "Refresh" : "Generate"} Thesis
            </>
          )}
        </Button>
      </div>

      {!thesis ? (
        <Card className="bg-muted/30">
          <CardContent className="p-8 text-center">
            <Brain className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No thesis generated yet</h3>
            <p className="text-muted-foreground mb-4">
              Click &quot;Generate Thesis&quot; to create this week&apos;s {name} swing trade analysis
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Thesis Banner */}
          <Card className={`${biasConfig.color} text-white`} data-testid={`${instrument.toLowerCase()}-thesis-banner`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <BiasIcon className="w-12 h-12" />
                  <div>
                    <div className="text-3xl font-bold">{biasConfig.text}</div>
                    <div className="opacity-90">Week {thesis.week_key}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm opacity-90">Conviction Score</div>
                  <div className="text-4xl font-bold">{thesis.conviction}/100</div>
                </div>
              </div>
              {thesis.entry_plan?.thesis_summary && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-sm opacity-90">{thesis.entry_plan.thesis_summary}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="brief" className="w-full">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="brief">Brief</TabsTrigger>
              <TabsTrigger value="cot">COT</TabsTrigger>
              <TabsTrigger value="technicals">Technicals</TabsTrigger>
              <TabsTrigger value="macro">Macro</TabsTrigger>
              <TabsTrigger value="debate">Debate</TabsTrigger>
            </TabsList>

            {/* Brief Tab */}
            <TabsContent value="brief" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Entry Plan */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4" /> Entry Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entry</span>
                      <span className="font-mono font-medium">{formatPrice(thesis.entry_plan?.entry, instrument)}</span>
                    </div>
                    <div className="flex justify-between text-red-500">
                      <span>Stop Loss</span>
                      <span className="font-mono font-medium">{formatPrice(thesis.entry_plan?.stop_loss, instrument)}</span>
                    </div>
                    <div className="flex justify-between text-green-500">
                      <span>TP1 (1:1)</span>
                      <span className="font-mono font-medium">{formatPrice(thesis.entry_plan?.tp1, instrument)}</span>
                    </div>
                    <div className="flex justify-between text-green-500">
                      <span>TP2 (2:1)</span>
                      <span className="font-mono font-medium">{formatPrice(thesis.entry_plan?.tp2, instrument)}</span>
                    </div>
                    <div className="flex justify-between text-green-500">
                      <span>TP3 (3:1)</span>
                      <span className="font-mono font-medium">{formatPrice(thesis.entry_plan?.tp3, instrument)}</span>
                    </div>
                    {thesis.entry_plan?.invalidation && (
                      <div className="pt-3 border-t">
                        <span className="text-xs text-amber-500 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Invalidation
                        </span>
                        <p className="text-sm mt-1">{thesis.entry_plan.invalidation}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Position Sizing */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Scale className="w-4 h-4" /> Position Sizing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Account Size</span>
                      <span className="font-medium">${thesis.sizing?.account_size?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Risk %</span>
                      <span className="font-medium">{thesis.sizing?.risk_pct_used}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Risk Amount</span>
                      <span className="font-medium">${thesis.sizing?.risk_amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stop Distance</span>
                      <span className="font-medium">{thesis.sizing?.stop_pips} pips</span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                      <span className="font-medium">Position Size</span>
                      <span className={`font-bold ${thesis.sizing?.blocked ? 'text-red-500' : 'text-green-500'}`}>
                        {thesis.sizing?.blocked ? "BLOCKED" : `${thesis.sizing?.lots} lots`}
                      </span>
                    </div>
                    {thesis.sizing?.blocked && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
                        <span className="text-xs text-red-500">{thesis.sizing?.block_reason}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Factor Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Conviction Factors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {thesis.factor_scores?.factors && Object.entries(thesis.factor_scores.factors).map(([key, val]) => (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</span>
                          <span className="font-medium">{val.score}</span>
                        </div>
                        <Progress value={val.score} className="h-2" />
                        <span className="text-[10px] text-muted-foreground">{val.weight}% weight</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* COT Tab */}
            <TabsContent value="cot" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Commitment of Traders</CardTitle>
                  <CardDescription>Speculator (Non-Commercial) positioning from CFTC</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">Net Position</span>
                      <div className={`text-xl font-bold ${thesis.cot?.nc_net >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {thesis.cot?.nc_net?.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">Z-Score</span>
                      <div className="text-xl font-bold">{thesis.cot?.nc_net_zscore}</div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">Percentile</span>
                      <div className="text-xl font-bold">{thesis.cot?.nc_net_pct_rank}%</div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">WoW Change</span>
                      <div className={`text-xl font-bold ${thesis.cot?.wow_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {thesis.cot?.wow_change?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-lg py-1">
                      {thesis.cot?.positioning_bias}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Report Date: {thesis.cot?.report_date}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Technicals Tab */}
            <TabsContent value="technicals" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Technical Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">Current Price</span>
                      <div className="text-xl font-bold font-mono">
                        {formatPrice(thesis.technicals?.current_price, instrument)}
                      </div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">Trend</span>
                      <div className="text-xl font-bold">{thesis.technicals?.trend}</div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">RSI (14)</span>
                      <div className="text-xl font-bold">{thesis.technicals?.rsi_14}</div>
                    </div>
                    <div className="bg-muted/30 p-3 rounded-lg">
                      <span className="text-xs text-muted-foreground">ATR (pips)</span>
                      <div className="text-xl font-bold">{thesis.technicals?.atr_pips}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                    <div>
                      <span className="text-xs text-muted-foreground">20 DMA</span>
                      <div className="font-mono">{formatPrice(thesis.technicals?.dma_20, instrument)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">50 DMA</span>
                      <div className="font-mono">{formatPrice(thesis.technicals?.dma_50, instrument)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">100 DMA</span>
                      <div className="font-mono">{formatPrice(thesis.technicals?.dma_100, instrument)}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">200 DMA</span>
                      <div className="font-mono">{formatPrice(thesis.technicals?.dma_200, instrument)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Macro Tab */}
            <TabsContent value="macro" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> DXY (Dollar Index)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price</span>
                      <span className="font-mono font-medium">{thesis.macro?.dxy?.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Trend</span>
                      <Badge variant="outline">{thesis.macro?.dxy?.trend}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">1D Change</span>
                      <span className={thesis.macro?.dxy?.change_1d >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {thesis.macro?.dxy?.change_1d}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">1W Change</span>
                      <span className={thesis.macro?.dxy?.change_1w >= 0 ? 'text-green-500' : 'text-red-500'}>
                        {thesis.macro?.dxy?.change_1w}%
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="w-4 h-4" /> Yield & Volatility
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Yield Differential</span>
                      <span className="font-medium">{thesis.macro?.yields?.differential}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Favors</span>
                      <Badge variant="outline">{thesis.macro?.yields?.favors}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">VIX</span>
                      <span className={`font-medium ${thesis.macro?.vix < 20 ? 'text-green-500' : thesis.macro?.vix > 25 ? 'text-red-500' : ''}`}>
                        {thesis.macro?.vix}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Debate Tab */}
            <TabsContent value="debate" className="mt-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-green-500">
                    <TrendingUp className="w-4 h-4" /> Scout Analysis
                  </CardTitle>
                  <CardDescription>Bull/Bear case identification</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-lg">
                      {thesis.scout_analysis}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-amber-500">
                    <Shield className="w-4 h-4" /> Skeptic Analysis
                  </CardTitle>
                  <CardDescription>Risk officer challenges</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-lg">
                      {thesis.skeptic_analysis}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-primary">
                    <Brain className="w-4 h-4" /> Orchestrator Decision
                  </CardTitle>
                  <CardDescription>Final synthesized thesis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-primary/10 p-4 rounded-lg">
                    <div className="flex items-center gap-4 mb-3">
                      <Badge className={biasConfig.color}>{thesis.orchestrator_synthesis?.bias}</Badge>
                      <span className="font-bold">Conviction: {thesis.orchestrator_synthesis?.conviction}/100</span>
                    </div>
                    <p className="text-sm">{thesis.orchestrator_synthesis?.thesis_summary}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default AuraFXPage;
